import type { IGraphNode } from './IGraphNode';

/**
 * Радіальне розташування графа (прототип v4, навігатор зв'язків): центр посередині, перший рівень — рівномірно по еліпсу (від 12-ї
 * години за годинниковою стрілкою), наступні рівні — віялом у секторі свого батька на більшому радіусі; радіуси вписують найдальший
 * рівень у полотно (по ширині — до 1,9 висоти). Чиста функція.
 */
export function relationsLayout(nodes: readonly IGraphNode[], width: number, height: number): Record<string, { x: number; y: number }> {
  const cx = width / 2;
  const cy = height / 2;
  const maxLevel = Math.max(0, ...nodes.map((n) => n.level));
  const k = 1 + Math.max(0, maxLevel - 1) * 0.55;
  const ry = Math.max(60, (height / 2 - 46) / k);
  const rx = Math.min(Math.max(80, (width / 2 - 90) / k), ry * 1.9);
  const pos: Record<string, { x: number; y: number }> = {};
  const angle: Record<string, number> = {};
  const span: Record<string, number> = {};
  const center = nodes.find((n) => n.level === 0);
  if (center) { pos[center.id] = { x: cx, y: cy }; angle[center.id] = -Math.PI / 2; span[center.id] = Math.PI * 2; }
  for (let level = 1; level <= maxLevel; level += 1) {
    const ring = nodes.filter((n) => n.level === level);
    const byParent = new Map<string, IGraphNode[]>();
    for (const n of ring) {
      const parent = n.parent ?? center?.id ?? '';
      byParent.set(parent, [...(byParent.get(parent) ?? []), n]);
    }
    const scale = 1 + (level - 1) * 0.55;
    for (const [parent, kids] of byParent) {
      const base = angle[parent] ?? -Math.PI / 2;
      const width0 = level === 1 ? Math.PI * 2 : Math.min(span[parent] ?? Math.PI / 3, Math.PI / 2);
      const step = width0 / Math.max(1, level === 1 ? kids.length : kids.length + 1);
      kids.forEach((n, i) => {
        const a = level === 1 ? base + step * i : base - width0 / 2 + step * (i + 1);
        angle[n.id] = a; span[n.id] = step;
        pos[n.id] = { x: Math.round(cx + rx * scale * Math.cos(a)), y: Math.round(cy + ry * scale * Math.sin(a)) };
      });
    }
  }
  return pos;
}
